import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cannes/chauffeur-cannes.png";
import { cityPriceFaq, citySeo } from "../slugs/cityPricing";

const cannes: ChauffeurPage = {
  slug: "cannes",
  title: "Private chauffeur Cannes – Luxury VTC",
  subtitle:
    "Private chauffeur service based in Cannes, active across the Riviera.",
  keywords: ["chauffeur cannes", "vtc cannes", "cannes film festival"],
  hero: {
    title: "VTC & private chauffeur in Cannes",
    subtitle: "Travel, events and transfers across the Cannes area.",
    keywords: ["VTC Cannes", "Croisette chauffeur", "French Riviera chauffeur"],
    imageSrc: heroImage.src,
    imageAlt: "VTC & private chauffeur in Cannes",
    badges: ["Event access", "Adaptable fleet", "Bilingual chauffeurs"],
  },
  benefits: {
    heading: "Why Cannes with us",
    items: [
      {
        title: "Event access",
        description:
          "MIPIM, Lions, the Festival: expert logistics in the heart of Cannes.",
      },
      {
        title: "Adaptable fleet",
        description: "Sedan, SUV, van, prestige.",
      },
      {
        title: "Bilingual chauffeurs",
        description: "FR/EN, premium welcome.",
      },
    ],
  },
  services: {
    heading: "Services in Cannes",
    services: [
      {
        title: "Event shuttles",
        text: "Coordination for teams and guests (Cannes & nearby).",
      },
      {
        title: "Hourly hire",
        text: "By the hour or by the day, private chauffeur in Cannes.",
      },
    ],
  },
  locations: {
    heading: "Key areas",
    items: [
      { name: "La Croisette" },
      { name: "Palais des Festivals" },
      { name: "Le Suquet" },
    ],
  },
  generic: {
    heading: "Cannes, smoothly",
    subheading:
      "Privileged access and proven logistics during the trade shows.",
    paragraphs: [
      "We handle arrivals, pick-ups and travel during the major events with centralised steering.",
      "This service is built for the intense pace of Cannes: hotels, the Palais, restaurants, private beaches and business meetings.",
      "Chauffeur coordination cuts downtime and keeps a consistently premium experience for your guests.",
    ],
    bullets: ["Badging & access", "Optimised queueing", "Radio coordination"],
  },
  process: {
    heading: "Organising a chauffeur in Cannes",
    steps: [
      "Define the sensitive points (Palais, hotels, meeting venues).",
      "Schedule vehicle rotations to match your agenda.",
      "Live synchronisation with your point of contact.",
      "On-the-ground adjustments for traffic and event constraints.",
    ],
  },
  faq: [
    cityPriceFaq("en", "Cannes", "CANNES"),
    {
      q: "Can you cover a full day of meetings in Cannes?",
      a: "Yes, hourly hire lets you chain trips with the same chauffeur and an optimised schedule.",
    },
    {
      q: "Is the service suited to the major Cannes events?",
      a: "Yes, we operate regularly during trade-show, festival and congress periods with dedicated organisation.",
    },
    {
      q: "Can you manage several vehicles at once?",
      a: "Yes, we plan multi-vehicle fleets for teams and VIP guests.",
    },
  ],
  relatedLinks: [
    {
      label: "Chauffeur during the Cannes Film Festival",
      href: "/en/chauffeur/cannes-film-festival",
    },
    {
      label: "Cannes to Monaco, flat fare from 200 EUR",
      href: "/en/chauffeur/transfers/cannes-monaco",
    },
    {
      label: "Cannes to Saint-Tropez, from 250 EUR",
      href: "/en/chauffeur/transfers/cannes-saint-tropez",
    },
    { label: "Chauffeur for events", href: "/en/chauffeur/events" },
    { label: "Corporate chauffeur", href: "/en/chauffeur/business" },
    {
      label: "Private car service at Nice Airport",
      href: "/en/chauffeur/airport",
    },
    {
      label: "Wedding car in Cannes",
      href: "/en/chauffeur/wedding-car-cannes",
    },
  ],
  cta: {
    title: "Chauffeur in Cannes",
    text: "Get in touch: we operate primarily in Cannes.",
    primaryHref: "/contact",
    primaryLabel: "Get an offer",
  },
  seo: {
    // MESURE 2026-10-09 : titre sans prix, page en position 36 à 45.
    ...citySeo("en", "Cannes", "CANNES", ", car service"),
    canonical: "/en/chauffeur/cannes",
  },
};

export default cannes;
